/**
 * La tabella delle allerte: che cosa e' scaduto, e che cosa manca per rimediare.
 *
 * Sta in un componente suo da quando Scadenzario e Allerte sono una pagina
 * sola (`pagine/ScadenzeEAllerte.tsx`): la pagina tiene lo stato e le modali,
 * qui c'e' la tabella a gruppi con le righe che bastano a decidere senza
 * cambiare pagina.
 *
 * Il sostituto si carica dalla riga
 * ---------------------------------
 * «Carica» apre la modale di caricamento sull'obbligo della riga, e un file
 * trascinato sulla riga la apre gia' compilata. Il trascinamento e' un'aggiunta
 * — da tastiera e con uno screen reader la via e' il pulsante. Sulle righe
 * «File da sostituire» il gesto e' invece «Sostituisci», sul singolo file: il
 * nuovo prende il posto del vecchio, non gli si affianca.
 */

import { useState } from "react";
import type { DragEvent } from "react";
import { Link } from "react-router-dom";
import type { IdNodo, TipoAllerta, Valutazione } from "../api/types";
import { partiPercorso, SEPARATORE_PERCORSO } from "../lib/alberatura";
import type { CatenePerId } from "../lib/alberatura";
import { ETICHETTA_TIPO_ALLERTA, naturaPertinente, puoConfermareNatura } from "../lib/allerte";
import type { Allerta } from "../lib/allerte";
import { ETICHETTE_CADENZA } from "../lib/scadenze";
import { Bottone, Chip, ChipNodo } from "./primitive";
import type { Tono } from "./primitive";
import { ProvenienzaCadenza } from "./ProvenienzaCadenza";

const TONO_GRAVITA: Record<Allerta["gravita"], Tono> = {
  critico: "critico",
  attenzione: "attenzione",
  informativo: "neutro",
};

/**
 * Una parola sola per la gravita'.
 *
 * Il motivo per esteso sta sotto, come testo: un `Chip` e' un'etichetta, e una
 * frase intera dentro un chip allargava la prima colonna fino a spingere le
 * altre quattro fuori dal riquadro.
 */
const ETICHETTA_GRAVITA: Record<Allerta["gravita"], string> = {
  critico: "urgente",
  attenzione: "da verificare",
  informativo: "informativo",
};

/** Il ritardo in parole. `null` significa «non c'e' un termine», non «oggi». */
function testoResidui(giorni: number | null): string {
  if (giorni === null) return "nessun termine";
  if (giorni < 0) return `in ritardo di ${Math.abs(giorni)} gg`;
  if (giorni === 0) return "scade oggi";
  return `fra ${giorni} gg`;
}

/** Vero se cio' che si trascina sono file del sistema, non altro. */
function portaFile(evento: DragEvent<HTMLElement>): boolean {
  return Array.from(evento.dataTransfer.types).includes("Files");
}

/**
 * Spegne un file rilasciato nella tabella ma fuori da una riga che lo accetta.
 *
 * Senza, un rilascio mancato di poco — sull'intestazione, fra due righe, su una
 * riga con un verdetto — lasciava fare al browser il suo gesto predefinito:
 * aprire il file al posto della pagina, e con la pagina lo stato di lavoro.
 * Le righe che accettano il file lo gestiscono prima, e qui non si tocca cio'
 * che hanno gia' gestito.
 */
function spegniRilascio(evento: DragEvent<HTMLElement>) {
  if (!portaFile(evento) || evento.defaultPrevented) return;
  evento.preventDefault();
  evento.dataTransfer.dropEffect = "none";
}

export interface AzioniAllerta {
  onDecidi: (allerta: Allerta) => void;
  onRevoca: (valutazione: Valutazione) => void;
  onArchivia: (allerta: Allerta) => void;
  /** Apre il caricamento sull'obbligo della riga, con il file se e' stato rilasciato. */
  onCarica: (allerta: Allerta, file: File | null) => void;
  /** Apre la sostituzione di un file scaduto. */
  onSostituisci: (allerta: Allerta, idDocumento: number) => void;
  /** Apre il commento pubblico di un obbligo senza contenuti. */
  onCommenta: (allerta: Allerta) => void;
  /** Apre le regole dell'obbligo; solo per il Super Admin. */
  onRegole?: (allerta: Allerta) => void;
}

export function TabellaAllerte({
  gruppi,
  catene,
  propostePerNodo,
  eSuperAdmin,
  inCorso,
  regolePronte,
  azioni,
}: {
  gruppi: readonly { tipo: TipoAllerta; allerte: Allerta[] }[];
  catene: CatenePerId;
  propostePerNodo: ReadonlyMap<IdNodo, Valutazione>;
  eSuperAdmin: boolean;
  inCorso: boolean;
  /** Falso finche' le regole dichiarate non sono arrivate: «Regole» non si offre. */
  regolePronte: boolean;
  azioni: AzioniAllerta;
}) {
  const totale = gruppi.reduce((somma, gruppo) => somma + gruppo.allerte.length, 0);
  return (
    <div className="tabella-guscio" onDragOver={spegniRilascio} onDrop={spegniRilascio}>
      <table className="tabella">
        <caption className="visually-hidden">
          {totale} allerte, raggruppate per tipo e ordinate dalla piu' in ritardo.
        </caption>
        <thead>
          <tr>
            <th scope="col">Obbligo</th>
            <th scope="col">Evidenza</th>
            <th scope="col">Cadenza</th>
            <th scope="col">Termine</th>
            <th scope="col">
              <span className="tabella__azioni">Decisione</span>
            </th>
          </tr>
        </thead>
        {gruppi.map((gruppo) => (
          <tbody key={gruppo.tipo}>
            <tr>
              <th scope="colgroup" colSpan={5} style={{ position: "static" }}>
                {ETICHETTA_TIPO_ALLERTA[gruppo.tipo]} ({gruppo.allerte.length})
              </th>
            </tr>
            {gruppo.allerte.map((allerta) => (
              <RigaAllerta
                key={allerta.chiave}
                allerta={allerta}
                parti={partiPercorso(catene, allerta.id_nodo)}
                proposta={propostePerNodo.get(allerta.id_nodo)}
                puoConfermare={puoConfermareNatura(allerta, eSuperAdmin, propostePerNodo)}
                inCorso={inCorso}
                regolePronte={regolePronte}
                azioni={azioni}
              />
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
}

/**
 * Una riga, con tutto quello che serve a decidere senza cambiare pagina.
 *
 * Le evidenze non sono un ornamento: la riga delle annualita' e' la singola
 * informazione che risponde da sola a «c'e' un sostituto?», e la provenienza
 * della cadenza e' cio' che distingue un dato da un'ipotesi. Nasconderle in un
 * tooltip significherebbe chiedere all'operatore di decidere alla cieca.
 */
function RigaAllerta({
  allerta,
  parti,
  proposta,
  puoConfermare,
  inCorso,
  regolePronte,
  azioni,
}: {
  allerta: Allerta;
  parti: { antenati: string | null; denominazione: string };
  /** Proposta di natura in attesa di conferma su questo obbligo. */
  proposta?: Valutazione;
  /** Vero se chi guarda puo' tradurre quella proposta in regola. */
  puoConfermare: boolean;
  inCorso: boolean;
  regolePronte: boolean;
  azioni: AzioniAllerta;
}) {
  const verdetto = allerta.valutazione;
  const [sopra, setSopra] = useState(false);
  const daSostituire = allerta.tipo === "documento_da_sostituire";

  // La riga accetta un file dove offre «Carica», e non mentre un'altra
  // operazione e' in corso: il rilascio e' un modo diverso di premere quel
  // pulsante, non un comando in piu'. Sulle righe dei file scaduti il gesto e'
  // «Sostituisci», per file: un rilascio sulla riga non saprebbe quale.
  const accettaFile = !verdetto && !inCorso && !daSostituire;

  // Il rilascio reagisce ai soli file: un testo o un link trascinati per
  // sbaglio non devono evidenziare la riga ne' aprire nulla.
  const alTrascinamento = (evento: DragEvent<HTMLTableRowElement>, entra: boolean) => {
    if (!accettaFile || !portaFile(evento)) return;
    evento.preventDefault();
    if (entra) evento.dataTransfer.dropEffect = "copy";
    setSopra(entra);
  };

  return (
    <tr
      className={sopra ? "tabella__riga--rilascio" : undefined}
      onDragEnter={(evento) => alTrascinamento(evento, true)}
      onDragOver={(evento) => alTrascinamento(evento, true)}
      onDragLeave={(evento) => {
        // `dragleave` scatta anche passando da una cella all'altra: la riga si
        // spegne solo quando il puntatore esce davvero.
        if (evento.currentTarget.contains(evento.relatedTarget as Node | null)) return;
        alTrascinamento(evento, false);
      }}
      onDrop={(evento) => {
        if (!accettaFile || !portaFile(evento)) return;
        evento.preventDefault();
        setSopra(false);
        azioni.onCarica(allerta, evento.dataTransfer.files[0] ?? null);
      }}
    >
      <th scope="row">
        <ChipNodo {...parti} separatore={SEPARATORE_PERCORSO} />
        <span className="documento-contesto">
          <Chip tono={TONO_GRAVITA[allerta.gravita]}>{ETICHETTA_GRAVITA[allerta.gravita]}</Chip>
        </span>
        <span className="campo__aiuto">{allerta.motivo}</span>
      </th>

      <td className="testo-tenue">
        {daSostituire ? null : allerta.evidenza.annualita.length > 0 ? (
          <div className="dato">
            {allerta.evidenza.annualita
              .map((voce) =>
                voce.stato === "pubblicato" ? String(voce.anno) : `${voce.anno} (${voce.stato})`,
              )
              .join(" · ")}
          </div>
        ) : (
          <span className="campo__aiuto">Nessuna annualita' presente.</span>
        )}
        {allerta.evidenza.prima_pubblicazione && !daSostituire ? (
          <div className="campo__aiuto">
            Prima pubblicazione {allerta.evidenza.prima_pubblicazione}.
          </div>
        ) : null}
        {allerta.documenti?.length ? (
          <ul className="pila-stretta campo__aiuto">
            {allerta.documenti.map((documento) => (
              <li key={documento.id}>
                · {documento.titolo}
                {daSostituire && documento.in_correzione ? (
                  // Il sostituto c'e' gia': resta da pubblicarlo, non da
                  // caricarne un altro.
                  <>
                    {" "}
                    <Link to={`/documenti?nodo=${encodeURIComponent(allerta.id_nodo)}`}>
                      sostituto in bozza: da pubblicare
                    </Link>
                  </>
                ) : daSostituire && !verdetto ? (
                  <>
                    {" "}
                    <Bottone
                      variante="contorno"
                      dimensione="piccolo"
                      disabled={inCorso}
                      onClick={() => azioni.onSostituisci(allerta, documento.id)}
                      aria-label={`Sostituisci «${documento.titolo}»`}
                    >
                      Sostituisci
                    </Bottone>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {allerta.evidenza.data_ultimo_aggiornamento ? (
          <div className="campo__aiuto">
            Ultimo aggiornamento {allerta.evidenza.data_ultimo_aggiornamento}
            {allerta.evidenza.aggiornamento_risalito
              ? " — dato risalito dai contenuti sottostanti, non del nodo"
              : ""}
            .
          </div>
        ) : null}
        {allerta.natura ? (
          <div className="campo__aiuto">Natura dichiarata dal Comune: {allerta.natura}.</div>
        ) : proposta && naturaPertinente(allerta.tipo) ? (
          <div className="campo__aiuto">
            Proposta da confermare: {proposta.natura_proposta} — {proposta.decisa_da}.
          </div>
        ) : null}
        {allerta.nota_assenza ? (
          <div className="campo__aiuto">
            Sul portale{allerta.nota_assenza.il ? ` dal ${allerta.nota_assenza.il}` : ""}: «
            {allerta.nota_assenza.testo}»
          </div>
        ) : null}
      </td>

      <td className="testo-tenue">
        {allerta.tipo === "quinquennio" || daSostituire ? (
          // Fine della pubblicazione e scadenza del file non dipendono dalla
          // cadenza: mostrarla qui e' cio' che faceva leggere «annuale» come
          // durata dei documenti.
          <span className="campo__aiuto">non dipende dalla cadenza</span>
        ) : (
          <>
            {ETICHETTE_CADENZA[allerta.cadenza]}
            {/* La provenienza si dichiara sempre: e' la differenza fra un dato e
                un'ipotesi, e nessuno dei 121 obblighi ha una cadenza propria. */}
            <ProvenienzaCadenza fonte={allerta.fonte_cadenza} idNodoFonte={allerta.id_nodo_cadenza} />
          </>
        )}
      </td>

      <td className="tabella__numero">
        <span
          className={
            allerta.giorni_residui !== null && allerta.giorni_residui < 0 ? "campo__errore" : undefined
          }
        >
          {testoResidui(allerta.giorni_residui)}
        </span>
        {allerta.termine ? (
          <div className="campo__aiuto">
            {allerta.tipo === "quinquennio"
              ? `${allerta.termine} — ultimo giorno di pubblicazione`
              : daSostituire
                ? `scaduto il ${allerta.termine}`
                : allerta.termine}
          </div>
        ) : null}
      </td>

      <td className={verdetto ? "tabella__verdetto" : "tabella__azioni"}>
        {verdetto ? (
          <div className="pila-stretta">
            <span className="campo__aiuto">
              {verdetto.esito === "sostituto_in_preparazione"
                ? `Atteso entro ${verdetto.valida_fino_al}`
                : "Sostituto non dovuto"}
              {verdetto.portata === "obbligo" ? " — natura dell'obbligo" : ""}
            </span>
            <span className="campo__aiuto">«{verdetto.motivo}»</span>
            <span className="albero__meta">
              {verdetto.decisa_da} · {verdetto.decisa_il}
            </span>
            <div className="riga">
              {/* Con una proposta in attesa il comando non e' «Revoca» ma
                  «Conferma»: la riga ha un verdetto, quindi qui si renderebbe
                  il solo annullamento, e il Super Admin vedeva la proposta
                  senza avere il modo di accoglierla. */}
              {puoConfermare ? (
                <Bottone
                  variante="primario"
                  dimensione="piccolo"
                  disabled={inCorso}
                  onClick={() => azioni.onDecidi(allerta)}
                  aria-label={`Conferma la natura proposta per ${parti.denominazione}`}
                >
                  Conferma la natura
                </Bottone>
              ) : null}
              <Bottone
                variante="fantasma"
                dimensione="piccolo"
                disabled={inCorso}
                onClick={() => azioni.onRevoca(verdetto)}
                aria-label={`Revoca il verdetto su ${parti.denominazione}`}
              >
                Revoca
              </Bottone>
              {/* Anche con un verdetto — «in preparazione» e' la risposta tipica a
                  un obbligo vuoto — il commento resta sul portale, e deve
                  restare modificabile da qui. */}
              {allerta.tipo === "obbligo_scoperto" ? (
                <BottoneCommento allerta={allerta} parti={parti} inCorso={inCorso} azioni={azioni} />
              ) : null}
            </div>
          </div>
        ) : (
          <div className="riga">
            <Bottone
              variante="primario"
              dimensione="piccolo"
              disabled={inCorso}
              onClick={() => azioni.onDecidi(allerta)}
              aria-label={`Decidi su ${parti.denominazione}`}
            >
              Decidi
            </Bottone>
            {allerta.tipo === "quinquennio" && allerta.pubblicazione_id ? (
              <Bottone
                variante="contorno"
                dimensione="piccolo"
                disabled={inCorso}
                onClick={() => azioni.onArchivia(allerta)}
                aria-label={`Archivia il contenuto di ${parti.denominazione}`}
              >
                Archivia ora
              </Bottone>
            ) : null}
            {daSostituire ? null : (
              <Bottone
                variante="fantasma"
                dimensione="piccolo"
                disabled={inCorso}
                onClick={() => azioni.onCarica(allerta, null)}
                aria-label={`Carica un documento su ${parti.denominazione}`}
                title="Oppure trascina il file sulla riga"
              >
                Carica
              </Bottone>
            )}
            {allerta.tipo === "obbligo_scoperto" ? (
              <BottoneCommento allerta={allerta} parti={parti} inCorso={inCorso} azioni={azioni} />
            ) : null}
            {azioni.onRegole && allerta.tipo !== "quinquennio" && !daSostituire ? (
              <Bottone
                variante="fantasma"
                dimensione="piccolo"
                disabled={inCorso || !regolePronte}
                title={regolePronte ? undefined : "Caricamento delle regole dichiarate…"}
                onClick={() => azioni.onRegole?.(allerta)}
                aria-label={`Regole dell'obbligo ${parti.denominazione}`}
              >
                Regole dell'obbligo
              </Bottone>
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

/** Il commento pubblico di un obbligo senza contenuti: scriverlo o cambiarlo. */
function BottoneCommento({
  allerta,
  parti,
  inCorso,
  azioni,
}: {
  allerta: Allerta;
  parti: { denominazione: string };
  inCorso: boolean;
  azioni: AzioniAllerta;
}) {
  return (
    <Bottone
      variante="fantasma"
      dimensione="piccolo"
      disabled={inCorso}
      onClick={() => azioni.onCommenta(allerta)}
      aria-label={`Commento pubblico su ${parti.denominazione}`}
    >
      {allerta.nota_assenza ? "Modifica commento" : "Commento pubblico…"}
    </Bottone>
  );
}
